import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { PageProvider } from '@/components/pagebuilder/PageContext.jsx';
import { EditableRenderer } from '@/components/pagebuilder/EditableRenderer.jsx';
import { PageRenderer } from '@/components/pagebuilder/PageRenderer.jsx';
import { BuilderPalette } from '@/components/pagebuilder/BuilderPalette.jsx';
import { TreeView } from '@/components/pagebuilder/TreeView.jsx';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import NodeSettingsModal from '@/components/pagebuilder/NodeSettingsModal.jsx';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea.jsx';
import { Card } from '@/components/ui/card';
import { 
  Save, 
  ArrowLeft, 
  Eye, 
  Code,
  Undo,
  Redo,
  Layout,
  Sidebar,
  Mail,
  Download,
  Upload
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { DEFAULT_EMAIL_TEMPLATE } from '@/components/newsletter/defaultTemplate';

// Email-safe components that are allowed
const EMAIL_ALLOWED_COMPONENTS = [
  'container',
  'section', 
  'row',
  'heading',
  'text',
  'button',
  'banner',
  'image',
  'video',
  'divider',
  'spacer',
  'companyLogo',
  'companyBrand',
  'socials',
  'columns1',
  'columns2',
  'columns2_2575',
  'columns2_7525',
  'columns3',
  'columns3_252550',
  'columns3_255025',
  'columns3_502525',
  'columns4',
  // Email personalisation blocks
  'emailQr',
  'claimButton',
];

// Helper function to find a node by ID in the tree
function findNodeById(tree, targetId) {
  if (!tree) return null;
  
  const searchNode = (node) => {
    if (node.id === targetId) return node;
    if (node.children) {
      for (const child of node.children) {
        const found = searchNode(child);
        if (found) return found;
      }
    }
    return null;
  };
  
  return searchNode(tree.root || tree);
}

// Helper function to update a node in the tree
function updateNodeInTree(tree, targetId, updatedNode) {
  if (!tree) return tree;
  
  const updateNode = (node) => {
    if (node.id === targetId) {
      return { ...updatedNode, id: node.id, children: node.children };
    }
    if (node.children) {
      return {
        ...node,
        children: node.children.map(child => updateNode(child))
      };
    }
    return node;
  };
  
  if (tree.root) {
    return { ...tree, root: updateNode(tree.root) };
  }
  return updateNode(tree);
}

export default function SendmireinTemplateEditorVisual() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    subject: '',
    previewText: '',
    draftJson: { children: [] },
    status: 'DRAFT',
  });
  
  const [loading, setLoading] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [draftObj, setDraftObj] = useState({ children: [] });
  const [dirty, setDirty] = useState(false);
  const [builderTab, setBuilderTab] = useState('visual');
  const [showPalette, setShowPalette] = useState(true);
  const [showNavigator, setShowNavigator] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  // Prevent unexpected redirect back to dashboard right after save/publish
  const blockRedirectUntil = useRef(0);
  
  // Undo/Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Import file input ref
  const fileInputRef = useRef(null);
  
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 120, tolerance: 6 } }));
  
  useEffect(() => {
    if (id && id !== 'new' && token) {
      loadTemplate();
    } else if (id === 'new') {
      // Start with empty template but include mandatory footer
      const emptyTemplate = {
        root: {
          id: 'email-root',
          type: 'root',
          props: {},
          children: [
            {
              id: 'newsletter-footer',
              type: 'newsletterFooter',
              props: {
                backgroundColor: '#f8f9fa',
                textColor: '#6c757d',
                linkColor: '#007bff',
                companyName: 'Ihr Unternehmen',
                companyAddress: t('common.pagebuilder.defaultCompanyAddress')
              },
              children: []
            }
          ]
        }
      };
      setDraftObj(emptyTemplate);
      setHistory([JSON.stringify(emptyTemplate)]);
      setHistoryIndex(0);
      setDirty(false);
    }
  }, [id, token]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await api.newslettersTemplatesGet(token, id);
      setTemplate(data);
      
      // Parse draftJson
      let parsed = { children: [] };
      if (data.draftJson) {
        if (typeof data.draftJson === 'string') {
          try {
            parsed = JSON.parse(data.draftJson);
          } catch (e) {
            console.error('Failed to parse draftJson:', e);
          }
        } else {
          parsed = data.draftJson;
        }
      }
      
      // Ensure root structure exists
      if (!parsed.root && parsed.children) {
        parsed = {
          root: {
            id: 'email-root',
            type: 'root',
            props: {},
            children: parsed.children
          }
        };
      }
      
      setDraftObj(parsed);
      setHistory([JSON.stringify(parsed)]);
      setHistoryIndex(0);
    } catch (error) {
      console.error('Error loading template:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.templates.loadError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const pushHistory = (newObj) => {
    const newStr = JSON.stringify(newObj);
    const newHist = history.slice(0, historyIndex + 1);
    newHist.push(newStr);
    setHistory(newHist);
    setHistoryIndex(newHist.length - 1);
    setDirty(true);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setDraftObj(JSON.parse(history[newIndex]));
      setDirty(true);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setDraftObj(JSON.parse(history[newIndex]));
      setDirty(true);
    }
  };

  // Handler for adding a child node
  const handleAddChild = (parentId, nodeType) => {
    console.log('[AddChild] parentId:', parentId, 'nodeType:', nodeType);
    
    // Determine if node should have children
    const containerTypes = ['container', 'section', 'row', 'card', 'columns1', 'columns2', 'columns2_2575', 'columns2_7525', 'columns3', 'columns3_252550', 'columns3_255025', 'columns3_502525', 'columns4'];
    const shouldHaveChildren = containerTypes.includes(nodeType);
    
    // For column layouts, initialize with the appropriate number of empty children
    let initialChildren = undefined;
    if (nodeType === 'columns1') {
      initialChildren = [{ id: `col-${Date.now()}-0`, type: 'container', props: {}, children: [] }];
    } else if (nodeType.startsWith('columns2')) {
      initialChildren = [
        { id: `col-${Date.now()}-0`, type: 'container', props: {}, children: [] },
        { id: `col-${Date.now()}-1`, type: 'container', props: {}, children: [] },
      ];
    } else if (nodeType.startsWith('columns3')) {
      initialChildren = [
        { id: `col-${Date.now()}-0`, type: 'container', props: {}, children: [] },
        { id: `col-${Date.now()}-1`, type: 'container', props: {}, children: [] },
        { id: `col-${Date.now()}-2`, type: 'container', props: {}, children: [] },
      ];
    } else if (nodeType === 'columns4') {
      initialChildren = [
        { id: `col-${Date.now()}-0`, type: 'container', props: {}, children: [] },
        { id: `col-${Date.now()}-1`, type: 'container', props: {}, children: [] },
        { id: `col-${Date.now()}-2`, type: 'container', props: {}, children: [] },
        { id: `col-${Date.now()}-3`, type: 'container', props: {}, children: [] },
      ];
    } else if (shouldHaveChildren) {
      initialChildren = [];
    }
    
    // Set default props based on node type
    let defaultProps = {};
    if (nodeType === 'text') {
      defaultProps = { text: 'Doppelklick zum Bearbeiten' };
    } else if (nodeType === 'heading') {
      defaultProps = { text: 'Überschrift', level: 2 };
    } else if (nodeType === 'button') {
      defaultProps = { label: 'Button', url: '#', color: '#2563eb' };
    }
    
    const newNode = {
      id: `${nodeType}-${Date.now()}`,
      type: nodeType,
      props: defaultProps,
      children: initialChildren,
    };
    
    console.log('[AddChild] New node:', newNode);
    
    let found = false;
    const addToNode = (node) => {
      if (node.id === parentId) {
        found = true;
        console.log('[AddChild] Found parent:', node.id);
        // If adding to root, keep footer at the end
        if (parentId === 'email-root') {
          const children = node.children || [];
          const footerIndex = children.findIndex(c => c.type === 'newsletterFooter');
          if (footerIndex >= 0) {
            // Insert before footer
            const newChildren = [...children];
            newChildren.splice(footerIndex, 0, newNode);
            console.log('[AddChild] Inserted before footer');
            return { ...node, children: newChildren };
          }
        }
        // Normal add to end
        console.log('[AddChild] Added to end of children');
        return {
          ...node,
          children: [...(node.children || []), newNode]
        };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(child => addToNode(child))
        };
      }
      return node;
    };
    
    const newTree = draftObj.root 
      ? { ...draftObj, root: addToNode(draftObj.root) }
      : addToNode(draftObj);
    
    if (!found) {
      console.error('[AddChild] Parent not found:', parentId);
      return;
    }
    
    console.log('[AddChild] New tree:', newTree);
    setDraftObj(newTree);
    pushHistory(newTree);
  };

  // Handler for deleting a node
  const handleDeleteNode = (nodeId) => {
    const deleteFromNode = (node) => {
      if (node.children) {
        return {
          ...node,
          children: node.children
            .filter(child => child.id !== nodeId)
            .map(child => deleteFromNode(child))
        };
      }
      return node;
    };
    
    const newTree = draftObj.root
      ? { ...draftObj, root: deleteFromNode(draftObj.root) }
      : deleteFromNode(draftObj);
    
    setDraftObj(newTree);
    pushHistory(newTree);
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
  };

  // Handler for reordering nodes
  const handleReorder = (parentId, oldIndex, newIndex) => {
    const reorderInNode = (node) => {
      if (node.id === parentId && node.children) {
        const newChildren = [...node.children];
        const [moved] = newChildren.splice(oldIndex, 1);
        newChildren.splice(newIndex, 0, moved);
        return { ...node, children: newChildren };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(child => reorderInNode(child))
        };
      }
      return node;
    };
    
    const newTree = draftObj.root
      ? { ...draftObj, root: reorderInNode(draftObj.root) }
      : reorderInNode(draftObj);
    
    setDraftObj(newTree);
    pushHistory(newTree);
  };

  // Handler for updating node props
  const handleUpdateNodeProps = (nodeId, newProps) => {
    const updateNode = (node) => {
      if (node.id === nodeId) {
        return { ...node, props: { ...node.props, ...newProps } };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(child => updateNode(child))
        };
      }
      return node;
    };
    
    const newTree = draftObj.root
      ? { ...draftObj, root: updateNode(draftObj.root) }
      : updateNode(draftObj);
    
    setDraftObj(newTree);
    pushHistory(newTree);
  };

  // Duplicate a node (deep) and insert after original
  const handleDuplicateNode = (nodeId) => {
    const source = draftObj.root || draftObj;
    const used = new Set();
    (function collect(n){ if(!n) return; used.add(n.id); (n.children||[]).forEach(collect); })(source);
    const genId = () => { let id; do { id = 'n' + Math.random().toString(36).slice(2,9); } while(used.has(id)); used.add(id); return id; };
    const deepClone = (n) => ({ id: genId(), type: n.type, props: { ...(n.props||{}) }, children: Array.isArray(n.children)? n.children.map(deepClone) : undefined });
    let newId = null;
    const cloneInto = (n) => {
      if(!n.children) return false;
      const idx = n.children.findIndex(c=>c.id===nodeId);
      if(idx!==-1){ const dup = deepClone(n.children[idx]); newId = dup.id; n.children.splice(idx+1, 0, dup); return true; }
      return n.children.some(cloneInto);
    };
    const newTree = JSON.parse(JSON.stringify(draftObj));
    const rootNode = newTree.root || newTree;
    if(!cloneInto(rootNode)) return;
    setDraftObj(newTree);
    pushHistory(newTree);
    if(newId) setSelectedNodeId(newId);
  };

  const handleSave = async () => {
    if (!template.name.trim()) {
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.templates.nameRequired'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      
      const templateData = {
        ...template,
        draftJson: draftObj,
      };

      if (id === 'new') {
        const created = await api.newslettersTemplatesCreate(token, templateData);
        toast({
          title: t('sendmirein.common.success'),
          description: t('sendmirein.templates.createSuccess'),
        });
        // Navigate to the newly created template within the editor, but stay in builder
        if (created && created.id) {
          navigate(`/sendmirein/templates/${created.id}`, { replace: true });
        }
      } else {
        await api.newslettersTemplatesUpdate(token, id, templateData);
        toast({
          title: t('sendmirein.common.success'),
          description: t('sendmirein.templates.saveSuccess'),
        });
      }
      setDirty(false);
      // Block unintended redirects for a short period after saving
      blockRedirectUntil.current = Date.now() + 8000;
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.templates.saveFailed'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!template.name.trim()) {
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.templates.nameRequired'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      
      // Save and publish in one request
      const templateData = {
        ...template,
        draftJson: draftObj,
        status: 'PUBLISHED',
      };

      await api.newslettersTemplatesUpdate(token, id, templateData);
      
      toast({
        title: t('sendmirein.templates.publishedTitle'),
        description: t('sendmirein.templates.publishedDescription'),
      });
      setDirty(false);
      // Stay in editor; update local status for immediate feedback
      setTemplate((prev) => ({ ...prev, status: 'PUBLISHED' }));
      blockRedirectUntil.current = Date.now() + 8000;
    } catch (error) {
      console.error('Error publishing template:', error);
      toast({
        title: 'Fehler',
        description: t('common.alerts.templateCouldNotPublish'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Defensive guard: if something navigates to '/sendmirein' right after save/publish, push back to the editor
  useEffect(() => {
    const now = Date.now();
    if (now < blockRedirectUntil.current && location.pathname === '/sendmirein') {
      const target = `/sendmirein/templates/${id}`;
      if (id && location.pathname !== target) {
        navigate(target, { replace: true });
      }
    }
  }, [location.pathname, id, navigate]);

  // Export template
  const handleExport = async () => {
    if (id === 'new') {
      toast({
        title: 'Fehler',
        description: 'Bitte speichern Sie das Template zuerst.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const { raw } = await api.newslettersTemplatesExport(token, id);
      const blob = new Blob([raw], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter-template-${(template.name || 'template').replace(/[^a-z0-9-_]+/gi,'_')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ 
        title: t('sendmirein.common.exported'), 
        description: 'Template wurde exportiert.' 
      });
    } catch (error) {
      console.error('Export failed', error);
      toast({ 
        title: t('sendmirein.common.error'), 
        description: 'Export fehlgeschlagen.', 
        variant: 'destructive' 
      });
    }
  };

  // Trigger import file picker
  const triggerImport = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Handle import file
  const handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      await api.newslettersTemplatesImport(token, payload);
      toast({ 
        title: t('sendmirein.common.imported'), 
        description: 'Template wurde importiert.' 
      });
      // Reload templates list
      navigate('/sendmirein');
    } catch (error) {
      console.error('Import failed', error);
      toast({ 
        title: t('sendmirein.common.error'), 
        description: 'Import fehlgeschlagen.', 
        variant: 'destructive' 
      });
    } finally {
      // reset input to allow re-selecting same file
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle drag end for palette items and reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      console.log('[Drag] No drop target');
      return;
    }
    
    const data = active?.data?.current || {};
    const overId = String(over.id);
    
    console.log('[Drag] Event:', { activeId: active.id, overId, data });
    
    // Check if dragging from palette
    if (data.kind === 'palette') {
      const type = data.type;
      if (!type) {
        console.log('[Drag] No type in palette data');
        return;
      }
      
      console.log('[Drag] Palette item:', type, 'to', overId);
      
      // Handle drop-child zone (add as last child)
      if (overId.startsWith('drop-child:') || overId.startsWith('drop-child-end:')) {
        const parentId = overId.startsWith('drop-child-end:') 
          ? overId.slice('drop-child-end:'.length)
          : overId.slice('drop-child:'.length);
        console.log('[Drag] Adding as child to:', parentId);
        handleAddChild(parentId, type);
        return;
      }
      
      // Handle drop-before zone
      if (overId.startsWith('drop-before:')) {
        const refId = overId.slice('drop-before:'.length);
        console.log('[Drag] Adding before:', refId);
        handleAddSibling(refId, type, 'before');
        return;
      }
      
      // Handle drop-after zone
      if (overId.startsWith('drop-after:')) {
        const refId = overId.slice('drop-after:'.length);
        console.log('[Drag] Adding after:', refId);
        handleAddSibling(refId, type, 'after');
        return;
      }
      
      // Fallback: add to root (before footer)
      console.log('[Drag] Fallback: Adding to root');
      handleAddChild('email-root', type);
      return;
    }
    
    // Handle moving existing elements (not from palette)
    console.log('[Drag] Checking for element move...');
    
    // Check if dropping into a drop zone
    if (overId.startsWith('drop-child:') || overId.startsWith('drop-child-end:')) {
      const parentId = overId.startsWith('drop-child-end:') 
        ? overId.slice('drop-child-end:'.length)
        : overId.slice('drop-child:'.length);
      console.log('[Drag] Moving element to child of:', parentId);
      handleMoveElement(active.id, parentId, 'child');
      return;
    }
    
    if (overId.startsWith('drop-before:')) {
      const refId = overId.slice('drop-before:'.length);
      console.log('[Drag] Moving element before:', refId);
      handleMoveElement(active.id, refId, 'before');
      return;
    }
    
    if (overId.startsWith('drop-after:')) {
      const refId = overId.slice('drop-after:'.length);
      console.log('[Drag] Moving element after:', refId);
      handleMoveElement(active.id, refId, 'after');
      return;
    }
    
    // Handle reordering existing elements within same parent (sortable)
    if (active.id !== over.id) {
      console.log('[Drag] Checking for same-parent reorder...');
      // Find parent and indices
      const findParentAndIndex = (node, targetId, parent = null) => {
        if (node.id === targetId) {
          return { parent, node };
        }
        if (node.children) {
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            if (child.id === targetId) {
              return { parent: node, node: child, index: i };
            }
            const result = findParentAndIndex(child, targetId, node);
            if (result) return result;
          }
        }
        return null;
      };
      
      const activeInfo = findParentAndIndex(draftObj.root || draftObj, active.id);
      const overInfo = findParentAndIndex(draftObj.root || draftObj, over.id);
      
      // Only reorder if both have same parent
      if (activeInfo?.parent && overInfo?.parent && 
          activeInfo.parent.id === overInfo.parent.id &&
          typeof activeInfo.index === 'number' && typeof overInfo.index === 'number') {
        console.log('[Drag] Same parent reorder');
        handleReorder(activeInfo.parent.id, activeInfo.index, overInfo.index);
      } else {
        console.log('[Drag] Different parents - no action');
      }
    }
  };
  
  // Handler for moving an existing element
  const handleMoveElement = (elementId, targetId, position) => {
    console.log('[MoveElement] elementId:', elementId, 'targetId:', targetId, 'position:', position);
    
    // First, find and remove the element from its current location
    let movedElement = null;
    const removeElement = (node) => {
      if (node.children) {
        const index = node.children.findIndex(c => c.id === elementId);
        if (index >= 0) {
          movedElement = node.children[index];
          console.log('[MoveElement] Found element to move:', movedElement);
          const newChildren = [...node.children];
          newChildren.splice(index, 1);
          return { ...node, children: newChildren };
        }
        return {
          ...node,
          children: node.children.map(child => removeElement(child))
        };
      }
      return node;
    };
    
    let treeAfterRemoval = draftObj.root 
      ? { ...draftObj, root: removeElement(draftObj.root) }
      : removeElement(draftObj);
    
    if (!movedElement) {
      console.error('[MoveElement] Element not found:', elementId);
      return;
    }
    
    // Now insert it at the new location
    const insertElement = (node) => {
      if (position === 'child' && node.id === targetId) {
        // Add as last child
        console.log('[MoveElement] Adding as child to:', node.id);
        return {
          ...node,
          children: [...(node.children || []), movedElement]
        };
      }
      
      if (node.children) {
        const index = node.children.findIndex(c => c.id === targetId);
        if (index >= 0 && (position === 'before' || position === 'after')) {
          console.log('[MoveElement] Inserting at index:', position === 'before' ? index : index + 1);
          const newChildren = [...node.children];
          if (position === 'before') {
            newChildren.splice(index, 0, movedElement);
          } else {
            newChildren.splice(index + 1, 0, movedElement);
          }
          return { ...node, children: newChildren };
        }
        return {
          ...node,
          children: node.children.map(child => insertElement(child))
        };
      }
      return node;
    };
    
    const newTree = treeAfterRemoval.root 
      ? { ...treeAfterRemoval, root: insertElement(treeAfterRemoval.root) }
      : insertElement(treeAfterRemoval);
    
    console.log('[MoveElement] New tree:', newTree);
    setDraftObj(newTree);
    pushHistory(newTree);
  };

  // Handler for adding sibling node
  const handleAddSibling = (refId, nodeType, position) => {
    console.log('[AddSibling] refId:', refId, 'nodeType:', nodeType, 'position:', position);
    
    const newNode = {
      id: `${nodeType}-${Date.now()}`,
      type: nodeType,
      props: {},
      children: ['container', 'section', 'row', 'card'].includes(nodeType) ? [] : undefined,
    };
    
    console.log('[AddSibling] New node:', newNode);
    
    let found = false;
    const addSiblingToNode = (node, parentNode = null) => {
      // Check if any of this node's children is the reference
      if (node.children) {
        const index = node.children.findIndex(c => c.id === refId);
        if (index >= 0) {
          found = true;
          console.log('[AddSibling] Found ref in parent:', node.id, 'at index:', index);
          const newChildren = [...node.children];
          if (position === 'before') {
            newChildren.splice(index, 0, newNode);
            console.log('[AddSibling] Inserted before at index:', index);
          } else {
            newChildren.splice(index + 1, 0, newNode);
            console.log('[AddSibling] Inserted after at index:', index + 1);
          }
          return { ...node, children: newChildren };
        }
        // Recursively check children
        const updatedChildren = node.children.map(child => addSiblingToNode(child, node));
        if (JSON.stringify(updatedChildren) !== JSON.stringify(node.children)) {
          return { ...node, children: updatedChildren };
        }
      }
      return node;
    };
    
    const newTree = draftObj.root 
      ? { ...draftObj, root: addSiblingToNode(draftObj.root) }
      : addSiblingToNode(draftObj);
    
    if (!found) {
      console.error('[AddSibling] Reference node not found:', refId);
      return;
    }
    
    console.log('[AddSibling] New tree:', newTree);
    setDraftObj(newTree);
    pushHistory(newTree);
  };

  if (loading && id !== 'new') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <PageProvider>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="h-screen flex flex-col bg-muted">
          {/* Header */}
          <div className="bg-background border-b px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button type="button" size="sm" variant="ghost" onClick={() => navigate('/sendmirein')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('sendmirein.common.back')}
              </Button>
              <div className="border-l pl-4">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="font-semibold text-sm">
                  {id === 'new'
                    ? t('sendmirein.templates.visual.titleNewHeader')
                    : template.name || t('sendmirein.templates.visual.titleEditHeader')}
                </h1>
                <p className="text-xs text-muted-foreground">{t('sendmirein.templates.visual.headerSubtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {dirty && (
                <span className="text-xs text-orange-600">
                  {t('sendmirein.templates.visual.notSavedDot')}
                </span>
              )}
              
              <Button type="button" size="sm" variant="ghost" onClick={handleUndo} disabled={historyIndex <= 0}>
                <Undo className="w-4 h-4" />
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
                <Redo className="w-4 h-4" />
              </Button>
              
              <div className="border-l pl-2 flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowPalette(!showPalette)}>
                  <Layout className="w-4 h-4 mr-1" />
                  {t('sendmirein.templates.visual.palette')}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowNavigator(!showNavigator)}>
                  <Sidebar className="w-4 h-4 mr-1" />
                  {t('sendmirein.templates.visual.navigator')}
                </Button>
              </div>

              <div className="border-l pl-2 flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setShowPreview(true)}>
                  <Eye className="w-4 h-4 mr-1" />
                  {t('sendmirein.templates.visual.preview')}
                </Button>
                <Button type="button" size="sm" onClick={handleSave} disabled={loading}>
                  <Save className="w-4 h-4 mr-1" />
                  {t('sendmirein.common.save')}
                </Button>
                {id !== 'new' && template.status === 'DRAFT' && (
                  <Button type="button" size="sm" onClick={handlePublish} disabled={loading}>
                    {t('sendmirein.templates.visual.publish')}
                  </Button>
                )}
                <div className="h-6 w-px bg-gray-300 mx-2" />
                <Button type="button" size="sm" variant="outline" onClick={handleExport} disabled={id === 'new'}>
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={triggerImport}>
                  <Upload className="w-4 h-4 mr-1" />
                  Import
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleImportFile}
                />
              </div>
            </div>
          </div>

          {/* Metadata Panel */}
          <div className="bg-background border-b px-4 py-3">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">{t('sendmirein.templates.fields.name')}</Label>
                <Input
                  size="sm"
                  value={template.name}
                  onChange={(e) => { setTemplate({ ...template, name: e.target.value }); setDirty(true); }}
                  placeholder={t('sendmirein.templates.fields.namePlaceholder')}
                />
              </div>
              <div>
                <Label className="text-xs">{t('sendmirein.templates.fields.subject')}</Label>
                <Input
                  size="sm"
                  value={template.subject || ''}
                  onChange={(e) => { setTemplate({ ...template, subject: e.target.value }); setDirty(true); }}
                  placeholder={t('sendmirein.templates.fields.subjectPlaceholder')}
                />
              </div>
              <div>
                <Label className="text-xs">{t('sendmirein.templates.fields.previewText')}</Label>
                <Input
                  size="sm"
                  value={template.previewText || ''}
                  onChange={(e) => { setTemplate({ ...template, previewText: e.target.value }); setDirty(true); }}
                  placeholder={t('sendmirein.templates.fields.previewPlaceholder')}
                />
              </div>
              <div>
                <Label className="text-xs">{t('sendmirein.templates.fields.status')}</Label>
                <div className="px-3 py-2 bg-muted rounded text-xs">
                  {template.status === 'PUBLISHED'
                    ? t('sendmirein.templates.fields.statusPublished')
                    : t('sendmirein.templates.fields.statusDraft')}
                </div>
              </div>
            </div>
          </div>

          {/* Main Editor */}
          <div className="flex-1 flex overflow-hidden">
            {/* Palette */}
            {showPalette && (
              <div className="w-56 bg-background border-r overflow-hidden">
                <BuilderPalette
                  allowed={EMAIL_ALLOWED_COMPONENTS}
                  draggable={true}
                  onAdd={(type) => {
                    // Add to root (before footer)
                    handleAddChild('email-root', type);
                  }}
                />
              </div>
            )}

            {/* Canvas */}
            <div className="flex-1 overflow-auto bg-muted p-8">
              <div className="max-w-4xl mx-auto">
                <EditableRenderer
                  tree={draftObj}
                  selectedId={selectedNodeId}
                  setSelectedId={setSelectedNodeId}
                  onAddChild={handleAddChild}
                  onAddSibling={handleAddSibling}
                  onDelete={handleDeleteNode}
                  onDuplicate={handleDuplicateNode}
                  onReorder={handleReorder}
                  onUpdateNodeProps={handleUpdateNodeProps}
                  onOpenSettings={(nodeId) => setSelectedNodeId(nodeId)}
                  allowedComponents={EMAIL_ALLOWED_COMPONENTS}
                  lockedTypes={['newsletterFooter']}
                />
              </div>
            </div>

            {/* Right Sidebar: Navigator + Settings */}
            {showNavigator && (
              <div className="w-80 bg-background border-l overflow-hidden flex flex-col">
                {/* Navigator Section */}
                <div className="border-b">
                  <div className="px-3 py-2 bg-muted text-xs font-semibold">
                    {t('sendmirein.templates.visual.navigator')}
                  </div>
                  <div className="max-h-64 overflow-auto p-2">
                    <TreeView
                      tree={draftObj}
                      selectedId={selectedNodeId}
                      onSelect={setSelectedNodeId}
                      onUpdate={(newTree) => {
                        setDraftObj(newTree);
                        pushHistory(newTree);
                      }}
                    />
                  </div>
                </div>
                
                {/* Settings Section */}
                <div className="flex-1 overflow-hidden flex flex-col">
                  {selectedNodeId ? (
                    <NodeSettingsModal
                      node={draftObj && findNodeById(draftObj, selectedNodeId)}
                      isNewsletterEditor={true}
                      onChange={(changedProps) => {
                        // changedProps contains only the changed properties, merge them with existing props
                        const currentNode = findNodeById(draftObj, selectedNodeId);
                        if (currentNode) {
                          const updatedNode = {
                            ...currentNode,
                            props: { ...currentNode.props, ...changedProps }
                          };
                          const newTree = updateNodeInTree(draftObj, selectedNodeId, updatedNode);
                          setDraftObj(newTree);
                          pushHistory(newTree);
                        }
                      }}
                      onClose={() => setSelectedNodeId(null)}
                    />
                  ) : (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      {t('sendmirein.templates.visual.selectElement')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Preview Modal */}
          {showPreview && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
              <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{t('sendmirein.templates.visual.previewTitle')}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('sendmirein.templates.visual.previewSubject', {
                        subject: template.subject || t('sendmirein.templates.visual.noSubject'),
                      })}
                    </p>
                  </div>
                  <Button variant="ghost" onClick={() => setShowPreview(false)}>
                    {t('sendmirein.templates.visual.close')}
                  </Button>
                </div>
                <div className="flex-1 overflow-auto bg-muted">
                  <div className="max-w-2xl mx-auto" style={{ backgroundColor: '#f5f5f5', padding: '20px' }}>
                    <div style={{ backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      {(() => {
                        console.log('[Preview] Tree structure:', draftObj);
                        console.log('[Preview] Root children:', draftObj?.root?.children);
                        draftObj?.root?.children?.forEach((child, idx) => {
                          console.log(`[Preview] Child ${idx}:`, child.type, child.id, child);
                        });
                        return <PageRenderer tree={draftObj} meta={{ emailMode: true }} />;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DndContext>
    </PageProvider>
  );
}
